<script setup>
import { computed } from 'vue';

const props = defineProps({
  type: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value)
  },
  levelYear: { 
    type: String,
    required: true
  },  
  isStudent: {
    type: Boolean,
    default: false
  },
  region: { 
    type: String,
    default: ''
  },
  achievementData: {
    type: Object,
    default: () => ({})
  }
});

const allAchievementData = {
  literacy: {
    '초4': [
      { level: '4', achievements: ['맥락을 고려하여 어휘의 의미를 추론하고 단어 간 관계를 파악하여 적용할 수 있다.', '새로운 정보나 사회 문제를 다루는 다양한 글을 읽고 세부 내용을 파악하여 글의 구조에 따라 내용을 간추릴 수 있다.', '단서나 배경지식을 활용하여 생략된 내용을 추론할 수 있다.', '다양한 자료를 해석하여 새로운 상황의 문제를 해결할 수 있다.'] },
      { level: '3', achievements: ['맥락을 고려하여 어휘의 의미를 이해하고 단어 간 관계를 파악할 수 있다.', '글쓴이의 의도나 글의 목적을 고려하여 중심 내용을 파악하고 간추릴 수 있다.', '일상 소재를 다룬 다양한 매체의 세부 내용을 이해하여 생략된 내용을 추론할 수 있다.', '글, 그림, 도표 등의 자료를 분석하여 문제를 해결할 수 있다.'] },
      { level: '2', achievements: ['일상에서 자주 접하는 어휘의 의미를 이해할 수 있다.', '일상적이고 친숙한 소재를 다룬 글의 주요 내용을 파악할 수 있다.', '글, 그림, 도표 등의 자료에서 명확하게 드러난 정보를 찾을 수 있다.', '글에 드러난 단서를 통해 생략된 내용을 짐작할 수 있다.', '글의 내용을 활용하여 일상생활의 단순한 문제를 해결할 수 있다.'] },
      { level: '1', achievements: ['일상에서 자주 쓰는 단어의 뜻을 대략 이해할 수 있다.', '생활에서 친숙한 소재의 글을 읽고 대강의 내용을 확인할 수 있다.', '이야기 속 인물의 말이나 행동을 파악할 수 있으며 이어질 내용을 짐작할 수 있다.', '짧은 글을 읽고 자기 생각이나 느낌을 표현할 수 있다.'] }
    ],
    '초6': [
      { level: '4', achievements: ['어휘의 사전적 의미를 정확히 알고 학습과 삶의 맥락에서 정교하게 활용할 수 있다.', '다양한 자료를 분석하여 세부 내용을 정확하게 파악할 수 있다.', '폭넓은 주제의 글을 읽고 생략된 정보를 추론하여 해석할 수 있다.', '글의 내용과 글쓴이의 의도를 비판적으로 판단하고 삶과 연계된 문제에 적용하여 적절한 대안을 제시할 수 있다.'] },
      { level: '3', achievements: ['어휘의 맥락적 의미를 이해하고 상황에 따라 다양하게 적용할 수 있다.', '그림이나 도표가 포함된 자료를 분석하여 필요한 정보를 선별할 수 있다.', '자료에 포함된 글이나 그림 등을 단서로 활용하여 생략된 부분을 추론할 수 있다.', '글의 내용과 글쓴이의 의도를 판단하고 문제 해결을 위한 적절한 방법을 찾을 수 있다.'] },
      { level: '2', achievements: ['맥락을 살펴 어휘의 의미를 알맞게 짐작할 수 있다.', '친숙한 소재를 다룬 자료를 읽고 주요 정보를 찾거나 중심 내용을 파악할 수 있다.', '자료에 포함된 글이나 그림 등을 바탕으로 글에 드러나지 않은 내용을 예상할 수 있다.', '글의 내용과 글쓴이의 의도를 판단할 수 있다.'] },
      { level: '1', achievements: ['일상생활에서 자주 쓰는 어휘의 뜻을 이해할 수 있다.', '그림이나 글에서 명확하게 드러난 정보를 찾을 수 있다.', '친숙한 소재를 다룬 간단한 글을 읽고 중심 내용을 파악할 수 있다.', '짧은 이야기 속 인물의 말과 행동을 통해 생각과 감정을 짐작할 수 있다.'] }
    ],
    '중2': [
      { level: '4', achievements: ['일상과 학습 상황에서 사용되는 추상적인 어휘를 활용할 수 있다.', '다양한 분야의 글을 읽고, 배경지식을 활용하여 생략된 정보를 추론할 수 있다.', '글의 다양한 자료를 통합하여 종합적으로 이해할 수 있다. 관점의 차이를 파악하고 의견의 타당성을 평가할 수 있다.', '글의 내용을 새로운 상황에 적용하여 문제를 해결할 수 있다.'] },
      { level: '3', achievements: ['학습에 필요한 어휘의 의미와 쓰임을 이해할 수 있다.', '일상생활에서 사용하는 단어의 의미 관계를 파악할 수 있다.', '글의 내용을 문단별로 요약할 수 있다.', '다양한 종류의 글을 읽고, 세부 내용을 바탕으로 복합자료의 의미를 해석할 수 있다.', '글에서 생략된 내용을 추론할 수 있다.', '글의 내용을 주어진 상황에 적용할 수 있다.'] },
      { level: '2', achievements: ['일상생활에서 사용하는 어휘의 의미를 알고 상황에 맞게 사용할 수 있다.', '글에 제시된 사실적인 정보를 찾아내고 이해할 수 있다.', '사진이나 그림, 카드뉴스 등의 자료를 활용해서 관련된 글의 내용을 해석할 수 있다.', '쉽게 접할 수 있는 생활문제에 관한 글을 읽고 글쓴이의 의도를 파악할 수 있다.'] },
      { level: '1', achievements: ['자주 쓰는 어휘나 쉬운 관용 표현의 의미를 알 수 있다.', '일상적 어휘로 표현된 글을 읽고 내용을 파악할 수 있다.', '짧은 이야기 글의 중심 내용을 찾을 수 있다.', '관심 있는 분야나 그림, 사진이 포함된 정보 글을 읽고 필요한 정보를 파악할 수 있다.'] }
    ],
    '고1': [
      { level: '4', achievements: ['글의 맥락을 고려하여 추상적·전문적인 어휘의 의미를 추론할 수 있다.', '과학·기술 및 사회·경제 분야의 글을 읽고 세부 내용을 정확하게 파악하고 통합하거나 일반화할 수 있다.', '관점이 다른 글이나 도표, 통계 등을 비교·분석하여 비판적으로 평가할 수 있다.', '다양한 유형의 글을 읽고 전개 방식이나 서술 방법을 파악할 수 있다.'] },
      { level: '3', achievements: ['글의 맥락을 고려하여 추상적인 어휘의 의미를 이해할 수 있다.', '과학·기술 및 사회·경제 분야의 글을 읽고 제시된 자료를 바탕으로 드러나지 않은 정보를 추론할 수 있다.', '추가적인 정보가 있는 그래프나 도표 등의 자료를 글과 연결 지어 해석할 수 있다.', '다양한 관점이 포함된 글을 읽고 관점의 차이를 파악할 수 있다.'] },
      { level: '2', achievements: ['글의 맥락을 고려하여 일상적 어휘나 관용 표현의 의미를 이해하고 사용할 수 있다.', '일상과 밀접한 사회·과학 분야의 글에서 명시적으로 드러난 정보를 파악하고 중심 내용을 찾을 수 있다.', '도표나 이미지 등 다양한 시각 자료를 활용한 글의 핵심 내용을 이해할 수 있다.'] },
      { level: '1', achievements: ['일상적인 어휘나 쉬운 관용 표현의 의미를 이해하고, 맥락을 통해서 단어나 어구의 의미를 짐작할 수 있다.', '일상과 밀접한 글을 읽고 글의 목적을 파악할 수 있다.', '시각 자료에 담긴 정보를 찾을 수 있다.', '이야기 글을 읽고 인물이 처한 상황을 파악할 수 있다.'] }
    ]
  },
  numeracy: {
    '초4': [ 
      { level: '4', achievements: ['수리적 사고를 활용해 일상생활 속 다양한 상황의 해결책을 찾을 수 있다.', '도형의 특징을 이해하고 그 관계를 탐구하며, 길이, 무게, 들이와 같은 단위를 상황에 맞게 적용하여 문제를 해결할 수 있다.', '주어진 식이나 도형의 복잡한 배열에서 규칙을 추론해 결과를 예측하고, 자료를 분석해 표와 그래프로 나타내고 이해할 수 있다.'] },
      { level: '3', achievements: ['수에 대한 개념을 알고 있으며, 사칙연산을 활용하여 생활 속 다양한 상황을 해결할 수 있다.', '주어진 상황에 도형의 기본 개념을 활용할 수 있고, 무게와 들이 단위를 사용해 합과 차를 구하는 문제를 해결할 수 있다.', '주어진 식이나 도형의 배열에서 규칙과 관계를 찾을 수 있으며, 자료를 분석해 추론하고 그림그래프로 나타낼 수 있다.'] },
      { level: '2', achievements: ['일상생활에서 접하는 간단한 문제를 식으로 만들고 계산할 수 있다.', '도형의 기본 개념을 이해하고 있으며, 식이나 도형으로 나타낸 간단한 배열에서 변화와 규칙을 찾아낼 수 있다.', '직각, 예각, 둔각의 개념을 알고 구별할 수 있으며, 간단한 무게의 차를 계산할 수 있다.', '표와 그림에서 필요한 정보를 찾을 수 있다.'] },
      { level: '1', achievements: ['일상생활 속 시각 자료, 그림 등에 제시된 단순한 계산을 할 수 있다.', '배열에 있는 규칙을 일부 찾아낼 수 있으며, 그림에서 직각과 비교하여 예각과 둔각을 구별할 수 있다.', '제시된 자료를 간단한 기준에 따라 분류할 수 있다.'] }
    ],
    '초6': [
      { level: '4', achievements: ['다양한 상황 속 수학적 요소를 종합적으로 분석하여 문제를 해결할 수 있다.', '최대공약수와 최소공배수를 활용하여 실생활 문제를 해결할 수 있다.', '추론을 통해 비율과 가능성을 수학적으로 표현할 수 있다.', '평면도형의 넓이를 구하고 크기를 비교할 수 있다.', '다양한 데이터를 해석하여 중요한 정보를 파악하고, 이를 바탕으로 의사소통할 수 있다.'] },
      { level: '3', achievements: ['수와 도형의 개념을 활용하여 일상생활 속 다양한 분야의 문제를 해결할 수 있다.', '평균의 의미를 알고 일상생활에서 평균을 활용할 수 있다.', '문제 상황에서 두 수의 대응 관계를 식으로 나타낼 수 있다.', '그래프를 보고 필요한 정보를 파악할 수 있다.', '어림이 필요한 상황에서 어림값을 구할 수 있다.'] },
      { level: '2', achievements: ['일상생활 속 그림, 그래프, 표 등에 제시된 수와 도형의 개념을 이해할 수 있다.', '문제 상황에 맞는 식을 만들고, 자연수의 혼합 계산으로 실생활 문제를 해결할 수 있다.', '기초적인 수학적 추론을 통해 반복되는 배열과 규칙을 찾아 문제를 해결할 수 있다.', '일상생활에서 접하는 입체도형의 구성 요소를 찾을 수 있다.'] },
      { level: '1', achievements: ['일상생활에서 접하는 안내문이나 시각 자료에 있는 단순한 수와 도형의 개념을 이해할 수 있다.', '간단한 자연수의 혼합 계산이 가능하고 상황에 맞는 식을 세울 수 있다.', '수 배열에서 규칙을 찾고 다음 수를 예측할 수 있다.', '분류된 자료를 표로 나타내고 자룟값을 비교할 수 있다.'] }
    ],
    '중2': [
      { level: '4', achievements: ['여러 분야에서 적절한 수학적 개념과 방법, 추론을 통해 복합적인 문제를 효과적으로 해결할 수 있다.', '올바른 수학적 표현과 수리적 사고를 바탕으로 의사소통할 수 있다.', '새로운 상황에서 수학적 구조를 파악하여 다양한 형태로 제시된 정보를 해석하고 종합할 수 있으며, 이에 근거하여 합리적인 의사결정을 할 수 있다.'] },
      { level: '3', achievements: ['일상생활 맥락과 학습 상황에서 수학적 표현을 이해하고, 수학적 개념과 방법을 활용하여 문제를 해결할 수 있다.', '다양한 시각 자료와 변화하는 양 사이의 관계를 통해 정보를 파악하고 상황에 맞는 수학적 지식을 적용할 수 있다.', '제시된 수학적 정당화 과정을 이해하고 필요한 조건을 판단할 수 있다.'] },
      { level: '2', achievements: ['일상생활 맥락과 학습 상황에서 수학적 지식과 방법을 통해 간단한 문제를 해결할 수 있다.', '유리수의 사칙계산을 할 수 있고 문자를 사용한 식을 이해할 수 있으며 기본적인 방정식을 풀 수 있다.', '그림을 보고 도형을 구분하고, 단순한 도형의 넓이나 부피를 구할 수 있다.', '간단한 표와 그래프를 해석할 수 있다.'] },
      { level: '1', achievements: ['익숙한 일상생활 맥락과 학습 상황 속에서 자기 경험을 토대로 수와 도형에 대한 기초적인 개념을 이해할 수 있다.', '증가와 감소, 영상과 영하 등 자신의 생활과 밀접한 상황을 양수와 음수로 표현할 수 있다.', '시각 자료를 바탕으로 간단한 식의 계산을 할 수 있고 주변에서 볼 수 있는 사물을 간단한 도형으로 나타낼 수 있다.'] }
    ],
    '고1': [
      { level: '4', achievements: ['다양한 수학적 개념을 활용하여 공학·인문 등의 분야에서 나타난 상황을 해석하고, 복잡한 계산과 수학적 추론을 할 수 있다.', '변화하는 상황을 이해하여 관계를 식, 표, 그래프 등의 적절한 수학적 표현으로 나타내고 문제를 해결할 수 있다.', '자료에서 얻은 정보 간의 관계를 파악하고 수학적 근거를 바탕으로 의사소통할 수 있다.'] },
      { level: '3', achievements: ['일상생활 맥락과 학습 상황에서 실수의 대소 관계를 이해하고, 다항식의 계산을 할 수 있다.', '곱셈공식과 인수분해, 이차함수와 이차방정식의 관계 및 도형의 방정식을 활용하여 다양한 문제를 해결할 수 있다.', '확률의 뜻을 알고 자료를 맞게 해석하여 의사결정을 할 수 있다.'] },
      { level: '2', achievements: ['일상생활 맥락과 학습 상황에서 집합의 연산과 제곱근의 계산을 할 수 있다.', '단순한 자료를 비교하여 정보를 파악하고, 행렬의 연산을 할 수 있다.', '변화하는 두 양 사이의 규칙을 찾을 수 있다.', '간단한 도형의 성질을 이용하여 문제를 해결할 수 있다.', '합의 법칙 또는 곱의 법칙을 이용하여 간단한 경우의 수를 구할 수 있다.'] },
      { level: '1', achievements: ['익숙한 일상생활 맥락에서 정수와 유리수의 간단한 계산을 할 수 있다.', '문자를 사용한 식의 의미를 부분적으로 이해할 수 있다.', '도형에 대한 단순한 개념과 성질을 안다.', '자료를 통해 정보를 일부 찾을 수 있고, 간단한 경우의 수를 셀 수 있다.'] }
    ]
  },
};

// levelYear 값을 한국어 형태로 변환하는 공통 함수
const convertLevelYear = (levelYear, fullName = false) => {
  if (!levelYear) return '';
  
  const schoolType = levelYear.charAt(0);
  const grade = levelYear.substring(1);
  
  if (fullName) {
    return `${grade}학년`;
  } else {
    switch (schoolType) {
      case 'E': return `초${grade}`;
      case 'M': return `중${grade}`;
      case 'H': return `고${grade}`;
      default: return levelYear;
    }
  }
};

const achievementTableData = computed(() => {
  const koreanLevelYear = convertLevelYear(props.levelYear);
  return allAchievementData[props.type]?.[koreanLevelYear] || [];
});

const title = computed(() => {
  const subject = props.type === 'literacy' ? '문해력' : '수리력';
  const koreanLevelYear = convertLevelYear(props.levelYear, true);
  return `각 수준에서 기대되는 성취 수행 : ${subject} ${koreanLevelYear}`;
});
</script>

<template>
  <div class="step" :class="[props.type, props.region]"> 
    <h4 class="title-box" v-if="!props.isStudent">{{ title }}</h4>
    <table class="mt-5" :class="props.isStudent ? 'student' : ''"> 
      <thead>
        <tr>
          <th>수준</th>
          <th v-if="props.isStudent">기대 성취 수행*</th>
          <th v-else>기대 성취 수행</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in achievementTableData" :key="row.level"> 
          <td>{{ row.level }}</td>
          <td>
            <p v-for="(achievement, index) in row.achievements" :key="index">
              {{ achievement }}
            </p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.step {       
    table {              
        th {
            font-size: 20px;
            font-weight: 500; 
            &:nth-child(1) { 
                width: 130px;
            }   
            &:nth-child(2) {
                background-color: #FAFAFA;
            }                  
        } 
        td {    
            height: 110px;  
            padding: 16px;
            font-size: 18px;
            letter-spacing: -.25px;
            &:nth-child(1) { 
                font-size: 32px;
                font-weight: 500;
                text-align: center;
                align-items: center;
                padding: 0 !important;
            }
            &:nth-child(2) {padding-right: 0;}
            p {
                position: relative;
                padding-left: 15px;                            
                &::before {
                    content: '';
                    position: absolute;
                    width: 4px;
                    height: 4px;
                    background-color: #000;
                    border-radius: 100%;
                    left: 0;
                    top: 11px;                   
                }
            }
        }    
    }
    &.literacy {
        h4 {
            background-color: var(--result-light-blue);  
            border-bottom: 6px solid var(--result-blue);       
        }
        table {
            th:nth-child(1) { 
                background-color: rgba(var(--result-light-blue-rgb),.8);
                color: #fff;
            }   
            @for $i from 1 through 4 {
                tr:nth-child(#{$i}) td:nth-child(1)  {
                    $opacity: 0; // 기본 투명도 초기화

                    @if $i == 1 {
                        $opacity: 0.3;
                    } @else if $i == 2 {
                        $opacity: 0.2;
                    } @else if $i == 3 {
                        $opacity: 0.1;
                    } @else if $i == 4 {
                        $opacity: 0.05;
                    }
                    background-color: rgba(var(--result-light-blue-rgb), $opacity);
                }
            }
            &.student {
              td {padding: 16px 18px;}
            } 
        }  
        &.seoul {
          h4 {
             position: relative;
             &::after {
                content: '';
                position: absolute;
                background-image: url("@/assets/images/result/char_03.png");
                width: 90px;
                height: 95px;
                top: 50%;
                right: 10px;
                transform: translateY(-55%);
                background-repeat: no-repeat;
                background-size: contain;
             }
          }          
        }        
    }
    &.numeracy {
        h4 {
            background-color: var(--result-light-red);  
            border-bottom: 6px solid var(--result-red);       
        }
        table {
            th:nth-child(1) { 
                background-color: rgba(var(--result-light-red-rgb),.8);
                color: #fff;
            }   
            @for $i from 1 through 4 {
                tr:nth-child(#{$i}) td:nth-child(1)  {
                    $opacity: 0; // 기본 투명도 초기화

                    @if $i == 1 {
                        $opacity: 0.3;
                    } @else if $i == 2 {
                        $opacity: 0.2;
                    } @else if $i == 3 {
                        $opacity: 0.1;
                    } @else if $i == 4 {
                        $opacity: 0.05;
                    }
                    background-color: rgba(var(--result-light-red-rgb), $opacity);
                }
            }
            &.student {
              td {padding: 16px 28px 16px 18px; letter-spacing: 0;}
            } 
        }
        &.seoul {
          h4 {
             position: relative;
             &::after {
                content: '';
                position: absolute;
                background-image: url("@/assets/images/result/char_02.png");
                width: 120px;
                height: 120px;
                top: 50%;
                right: -5px;
                transform: translateY(-55%);
                background-repeat: no-repeat;
                background-size: contain;
             }
          }
        }
    }
}
</style>